import type { AccessibleBoard } from '@/entities/collab';

/**
 * How the catalog is ordered. The two "recent" orders are different facts and are never mixed up:
 *
 * - `opened`: when the viewer last OPENED the board on this device (a local visit history). A
 *   board never opened here has no such time and follows the opened ones, newest activity first;
 * - `updated`: the newest activity the viewer may see on the board (the server's
 *   `last_activity_at`), else the board's own last edit.
 */
export type BoardSort = 'opened' | 'updated' | 'name';

const activityAt = (board: AccessibleBoard) => board.last_activity_at ?? board.updated_at;

export function sortBoards(
  boards: readonly AccessibleBoard[],
  sort: BoardSort,
  openedIds: readonly string[],
  locale: string,
): AccessibleBoard[] {
  if (sort === 'name') {
    return boards.toSorted(
      (a, b) => a.name.localeCompare(b.name, locale) || a.id.localeCompare(b.id),
    );
  }
  if (sort === 'updated') {
    return boards.toSorted(
      (a, b) => activityAt(b).localeCompare(activityAt(a)) || a.id.localeCompare(b.id),
    );
  }
  const rank = new Map(openedIds.map((id, index) => [id, index]));
  return boards.toSorted((a, b) => {
    const ra = rank.get(a.id);
    const rb = rank.get(b.id);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return activityAt(b).localeCompare(activityAt(a)) || a.id.localeCompare(b.id);
  });
}
